import { injectScopedCss, markScopeRoot } from './css.js';
import { crashMessage, loadFailureMessage, SurfaceRuntimeError } from './errors.js';
import { requireSurfaceHost } from './host.js';
import { checkAssetUrl, loadSurfaceModule } from './loader.js';
import { OutputBus } from './output-bus.js';
import { isExtensionPointId, pickImplementer } from './point-target.js';
import { validateSurfaceValue } from './validate.js';
/**
 * How long the runtime waits for the promise a surface's `unmount()` returns before it releases
 * the mount's services and empties the element anyway, emitting `surface:unmount-timeout` (R-50).
 */
export const UNMOUNT_SETTLE_MS = 1_000;
let mountCounter = 0;
const busyElements = new Set();
let liveRootMountId = null;
/** Reset the mount counter, the busy-element set and the root claim. `resetSurfaceRuntime` in `./testing` is the only caller. */
export function resetMountRuntime() {
    mountCounter = 0;
    busyElements.clear();
    liveRootMountId = null;
}
/**
 * Mount the surface `target` names into `el`, through the installed host.
 *
 * Synchronous refusals, in this order: `el` already holds a live mount, `element-busy`; no host is
 * installed, `host-missing`. Both throw {@link SurfaceRuntimeError}. Otherwise the call returns a
 * {@link SurfaceMount} in `loading` and runs these steps (WS-2 §5):
 * 0. A point target `{ point, app? }` only (surface extension points, D9). A malformed point id, a
 *    non-string `app`, or a target that also carries `surface`, fails `unresolved` with no host call.
 *    Otherwise `host.implementers(point)` answers; a throw or a value that is not an array reads as
 *    `[]`. The runtime picks the implementer of `app`, else of the point owner (the id before the
 *    `/`); among two surfaces of that app, the first name by code unit. No pick fails `unresolved`.
 *    Step 1 then resolves the pick as the app arm `{ app, surface }`.
 * 1. `host.resolve(target)`. A `null`, a rejection, or a ref whose `appId`, arm or name does not
 *    match the target fails the mount `unresolved` (R-4). For a point target, a ref whose
 *    declaration is not an embedded, non-root surface that lists the point in `implements` fails
 *    `unresolved` too, whatever `implementers` answered.
 * 2. A root declaration while another root mount is live fails `root-busy`.
 * 3. `checkAssetUrl` on the bundle URL and a non-null stylesheet URL; a miss fails `bad-url`.
 * 4. The bundle loads and a module is picked; `load-failed` or `no-mount-export` on a miss.
 * 5. The stylesheet is injected and `el` is marked as a scope root. When this mount's fetch injected
 *    the stylesheet, each construct the scoper refused is one `surface:css-rejected` event, in
 *    stylesheet order (R-90). The events go out even when the host cancelled the mount during the
 *    fetch: the stylesheet is in the page, so the host's `report` still hears what it lacks.
 * 6. The input is validated. With no declared input, which includes every legacy ref, a supplied
 *    input is refused `no-input-declared`. A refused input mounts as `undefined` with a
 *    `surface:input-rejected` event.
 * 7. `host.servicesFor(ref, id, el)` mints services, after every refusal above. The surface's
 *    `mount` runs in a guard; a throw or a handle lacking `update` or `unmount` ends the mount
 *    `crashed` with `el` emptied and services released.
 *
 * A host that calls `update` during step 6 or 7 has the latest input delivered to the surface's
 * `update` once the mount is `mounted`, validated as a live update is.
 *
 * A host that calls `unmount` during step 6 (from a listener on `surface:input-rejected`) or step 7
 * (from inside `servicesFor`) cancels the mount: it ends `unmounted`, the surface never runs, the
 * element's attributes are restored and any services minted are released.
 *
 * Every failure emits one `surface:load-failed` with its code. A failed import adds `message`: the
 * import error's `name: message` with every URL replaced by `<url>`, cut to 500 characters
 * (`loadFailureMessage`, #3693). Every other failure emits no `message` key. Every runtime event goes to the
 * host's `report` and to `onRuntime` listeners; a throw from `report` is contained. A surface's
 * `ctx.emit` is validated against its declared outputs: an undeclared name, any `surface:` name or
 * any emit from a legacy ref is refused `undeclared`, a failing payload with the validator's
 * reason. `ctx.reportError` turns into `surface:crashed` with phase `render`, the handle's
 * `unmount` is called, and the mount ends `crashed` (R-3). Crash messages are cut to 500
 * characters. Emits and reports after a mount ended are dropped silently. The mount ends before
 * the handle's `unmount` is called, so this covers emits and reports made from inside it.
 *
 * Every crash and unmount of a live surface waits for a promise the handle's `unmount` returns,
 * at most {@link UNMOUNT_SETTLE_MS}, before it releases services and empties the element (R-50).
 * A crash emits its `surface:crashed` after that cleanup, so a host listener that renders a
 * fallback into the element keeps it. A rejection during a crash is ignored: the first message
 * is the one reported.
 */
export function mountSurface(el, target, options = {}) {
    if (busyElements.has(el)) {
        throw new SurfaceRuntimeError('element-busy', 'the element already holds a live surface mount');
    }
    const host = requireSurfaceHost();
    mountCounter += 1;
    const id = `${mountLabel(target)}#${mountCounter}`;
    busyElements.add(el);
    return new LiveMount(el, target, options.input, host, id);
}
function isPointTarget(target) {
    return Object.hasOwn(target, 'point');
}
/** The mount id before its counter: `<app>/<surface>`, `<app>/legacy`, `<point>` or `<point>@<app>`. */
function mountLabel(target) {
    if (!isPointTarget(target))
        return `${target.app}/${target.surface ?? 'legacy'}`;
    return target.app === undefined ? target.point : `${target.point}@${target.app}`;
}
/**
 * True when a point target is well formed: a point id of the grammar, an `app` that is absent or a
 * string, and no `surface` key, since a value with both `surface` and `point` is outside both arms.
 */
function isWellFormedPoint(target) {
    if (!isExtensionPointId(target.point) || Object.hasOwn(target, 'surface'))
        return false;
    return target.app === undefined || typeof target.app === 'string';
}
/** True when a resolved implementer's declaration is an embedded, non-root surface that lists `point` in `implements`. */
function implementsPoint(declaration, point) {
    if (declaration.kind !== 'embedded' || declaration.root === true)
        return false;
    return Array.isArray(declaration.implements) && declaration.implements.includes(point);
}
function matchesTarget(ref, target) {
    if (ref.appId !== target.app)
        return false;
    if (target.surface === null)
        return ref.declaration === null;
    return ref.declaration !== null && ref.declaration.name === target.surface;
}
function isHandle(value) {
    if (typeof value !== 'object' || value === null)
        return false;
    return typeof Reflect.get(value, 'update') === 'function' && typeof Reflect.get(value, 'unmount') === 'function';
}
class LiveMount {
    id;
    ready;
    #el;
    #target;
    #host;
    #bus;
    #resolveReady;
    #state = 'loading';
    #ref = null;
    #pendingInput;
    #inputDirty = false;
    #handle = null;
    #servicesMinted = false;
    #restoreAttributes = null;
    /** True from the start of the mount step, when the pending input is read, until the mount settles. */
    #mountStepStarted = false;
    #inMount = false;
    #pendingRenderCrash = null;
    #unmountAfterMount = false;
    constructor(el, target, input, host, id) {
        this.id = id;
        this.#el = el;
        this.#target = target;
        this.#host = host;
        this.#pendingInput = input;
        this.#bus = new OutputBus(id);
        const settled = Promise.withResolvers();
        this.ready = settled.promise;
        this.#resolveReady = settled.resolve;
        void this.#run();
    }
    get state() {
        return this.#state;
    }
    get ref() {
        return this.#ref;
    }
    on(output, listener) {
        return this.#bus.on(output, listener);
    }
    onRuntime(listener) {
        return this.#bus.onRuntime(listener);
    }
    update(input) {
        if (this.#state === 'loading') {
            this.#pendingInput = input;
            // Once the mount step has read the pending input, a newer one is delivered after mounting.
            this.#inputDirty = this.#mountStepStarted;
            return;
        }
        if (this.#state !== 'mounted')
            return;
        const value = this.#validateInput(input);
        // A listener on this update's surface:input-rejected may have ended the mount.
        if (this.#state !== 'mounted')
            return;
        try {
            this.#handle.update(value);
        }
        catch (thrown) {
            // A surface that reported a crash, or whose output listener unmounted it, before it threw
            // has ended already; its cleanup ran once and the first outcome stands.
            if (this.#state === 'mounted')
                this.#crash('update', crashMessage(thrown));
        }
    }
    unmount() {
        if (this.#inMount) {
            this.#unmountAfterMount = true;
            return;
        }
        if (this.#state === 'loading') {
            this.#state = 'unmounted';
            this.#release();
            this.#finish();
            return;
        }
        if (this.#state !== 'mounted')
            return;
        const handle = this.#handle;
        // The mount has ended before the surface's own unmount runs, so a re-entrant update, unmount,
        // emit or reportError from inside it is a no-op (architecture-10).
        this.#endLive('unmounted');
        let returned;
        try {
            returned = handle.unmount();
        }
        catch (thrown) {
            this.#state = 'crashed';
            this.#crashCleanup('unmount', crashMessage(thrown));
            return;
        }
        this.#afterSurfaceUnmount(returned, (rejection) => {
            this.#teardown();
            if (rejection !== null)
                this.#event({ name: 'surface:crashed', mountId: this.id, phase: 'unmount', message: rejection });
            this.#finish();
        });
    }
    /**
     * Step 0 for a point target: the app arm of the implementer the runtime picks, or `null`, which
     * fails the mount `unresolved`. The runtime asks after one microtask, so a refusal reaches
     * listeners the caller attached after the `mountSurface` call, as a failed resolve does.
     */
    async #pick(target) {
        await Promise.resolve();
        if (!isWellFormedPoint(target))
            return null;
        let answer;
        try {
            answer = this.#host.implementers(target.point);
        }
        catch {
            answer = [];
        }
        const pick = pickImplementer(answer, target.point, target.app);
        return pick === null ? null : { app: pick.app, surface: pick.surface };
    }
    async #run() {
        const target = this.#target;
        let appTarget;
        if (isPointTarget(target)) {
            const picked = await this.#pick(target);
            if (this.#state !== 'loading')
                return;
            if (picked === null)
                return this.#fail('unresolved');
            appTarget = picked;
        }
        else {
            appTarget = target;
        }
        let ref;
        try {
            ref = await this.#host.resolve(appTarget);
        }
        catch {
            ref = null;
        }
        if (this.#state !== 'loading')
            return;
        if (ref === null || !matchesTarget(ref, appTarget))
            return this.#fail('unresolved');
        // matchesTarget passed with a surface name, so the ref is a declared one.
        if (isPointTarget(target) && !implementsPoint(ref.declaration, target.point))
            return this.#fail('unresolved');
        this.#ref = ref;
        if (ref.declaration !== null && ref.declaration.root === true) {
            if (liveRootMountId !== null)
                return this.#fail('root-busy');
            liveRootMountId = this.id;
        }
        if (checkAssetUrl(ref.bundleUrl) === null || (ref.cssUrl !== null && checkAssetUrl(ref.cssUrl) === null)) {
            return this.#fail('bad-url');
        }
        let module;
        try {
            module = await loadSurfaceModule(ref, this.#host);
        }
        catch (thrown) {
            if (this.#state !== 'loading')
                return;
            if (!(thrown instanceof SurfaceRuntimeError))
                return this.#fail('load-failed');
            return this.#fail(thrown.code, Object.hasOwn(thrown, 'cause') ? loadFailureMessage(thrown.cause) : undefined);
        }
        if (this.#state !== 'loading')
            return;
        await injectScopedCss(ref.appId, ref.cssUrl, ({ construct, offset }) => this.#event({ name: 'surface:css-rejected', mountId: this.id, construct, offset }));
        if (this.#state !== 'loading')
            return;
        this.#mount(ref, module);
    }
    #mount(ref, module) {
        this.#restoreAttributes = markScopeRoot(this.#el, ref, this.id);
        this.#mountStepStarted = true;
        const input = this.#validateInput(this.#pendingInput);
        // A listener on the mount-time surface:input-rejected may have unmounted the mount.
        if (this.#abandonIfCancelled())
            return;
        let ctx;
        try {
            ctx = {
                input,
                services: this.#host.servicesFor(ref, this.id, this.#el),
                emit: (output, payload) => this.#emit(output, payload),
                reportError: (message) => this.#reportError(message),
            };
        }
        catch (thrown) {
            if (this.#abandonIfCancelled())
                return;
            return this.#crash('mount', crashMessage(thrown));
        }
        this.#servicesMinted = true;
        // The host's servicesFor may have unmounted the mount before it returned.
        if (this.#abandonIfCancelled())
            return;
        let handle;
        this.#inMount = true;
        try {
            handle = module.mount(this.#el, ctx);
        }
        catch (thrown) {
            this.#inMount = false;
            return this.#crash('mount', crashMessage(thrown));
        }
        this.#inMount = false;
        if (!isHandle(handle)) {
            return this.#crash('mount', new SurfaceRuntimeError('bad-handle', 'mount returned no handle with update and unmount').message);
        }
        this.#handle = handle;
        this.#state = 'mounted';
        this.#resolveReady('mounted');
        if (this.#pendingRenderCrash !== null)
            return this.#crash('render', this.#pendingRenderCrash);
        if (this.#unmountAfterMount)
            return this.unmount();
        if (this.#inputDirty)
            this.update(this.#pendingInput);
    }
    /**
     * After a host call made inside the mount step, true when the host unmounted the mount during it.
     * The cancelling `unmount` already settled `ready`, freed the element and the root claim, so this
     * only puts back the element's attributes and releases services minted before the cancel. It
     * never frees the element again: a host may already have mounted another surface into it.
     */
    #abandonIfCancelled() {
        if (this.#state === 'loading')
            return false;
        this.#restoreAttributes?.();
        this.#restoreAttributes = null;
        if (this.#servicesMinted) {
            this.#servicesMinted = false;
            this.#host.releaseServices(this.id);
        }
        return true;
    }
    #validateInput(input) {
        if (input === undefined)
            return undefined;
        const declared = this.#ref.declaration?.input;
        if (declared === undefined) {
            this.#event({ name: 'surface:input-rejected', mountId: this.id, path: '', reason: 'no-input-declared' });
            return undefined;
        }
        const verdict = validateSurfaceValue(declared, input);
        if (verdict.ok)
            return verdict.value;
        this.#event({ name: 'surface:input-rejected', mountId: this.id, path: verdict.path, reason: verdict.reason });
        return undefined;
    }
    #emit(output, payload) {
        if (this.#state !== 'mounted' && !this.#inMount)
            return;
        const outputs = this.#ref.declaration?.outputs;
        if (output.startsWith('surface:') || outputs === undefined || !Object.hasOwn(outputs, output)) {
            this.#event({ name: 'surface:output-rejected', mountId: this.id, output, path: '', reason: 'undeclared' });
            return;
        }
        const verdict = validateSurfaceValue(outputs[output], payload);
        if (!verdict.ok) {
            this.#event({ name: 'surface:output-rejected', mountId: this.id, output, path: verdict.path, reason: verdict.reason });
            return;
        }
        this.#bus.deliver(output, verdict.value);
    }
    #reportError(message) {
        const text = crashMessage(message);
        if (this.#inMount) {
            this.#pendingRenderCrash ??= text;
            return;
        }
        if (this.#state === 'mounted')
            this.#crash('render', text);
    }
    #event(event) {
        try {
            this.#host.report(event);
        }
        catch {
            console.error(`[sdk-surfaces] the host's report threw on ${this.id}`);
        }
        this.#bus.deliverRuntime(event);
    }
    /** End the mount `failed` with one `surface:load-failed`; the event holds the `message` key only when `message` is a string. */
    #fail(code, message) {
        this.#state = 'failed';
        this.#release();
        this.#event(message === undefined ? { name: 'surface:load-failed', mountId: this.id, code } : { name: 'surface:load-failed', mountId: this.id, code, message });
        this.#finish();
    }
    /**
     * End the mount `crashed`. The state is final and the root claim free before the handle's
     * `unmount` runs, so a surface that reports, emits or is unmounted again from inside it changes
     * nothing: the cleanup runs once and the first message is the one reported (architecture-10).
     */
    #crash(phase, message) {
        const handle = this.#handle;
        this.#endLive('crashed');
        let returned;
        if (handle !== null) {
            try {
                returned = handle.unmount();
            }
            catch {
                // The mount is already crashing; the first message is the one reported.
            }
        }
        this.#afterSurfaceUnmount(returned, () => this.#crashCleanup(phase, message));
    }
    /** Set the final state of a mount that ran, drop its handle and free the root claim. */
    #endLive(state) {
        this.#handle = null;
        this.#state = state;
        this.#releaseRootClaim();
    }
    /** The cleanup after a crash, then its one `surface:crashed`, so a fallback a listener renders stays. */
    #crashCleanup(phase, message) {
        this.#teardown();
        this.#event({ name: 'surface:crashed', mountId: this.id, phase, message });
        this.#finish();
    }
    /**
     * Run `cleanup` once the surface's own unmount is done (R-50). `returned` is what the handle's
     * `unmount` returned: `undefined` runs `cleanup` now. Anything else is awaited as a promise for
     * at most {@link UNMOUNT_SETTLE_MS}; `cleanup` then receives the rejection's crash message, or
     * `null` when it settled or the deadline passed. The deadline emits `surface:unmount-timeout`
     * first. A settle after the deadline is ignored, and a rejection never goes unhandled.
     */
    #afterSurfaceUnmount(returned, cleanup) {
        if (returned === undefined)
            return cleanup(null);
        let timer;
        const deadline = new Promise((resolve) => {
            timer = setTimeout(() => resolve({ kind: 'timeout' }), UNMOUNT_SETTLE_MS);
        });
        const settled = Promise.resolve(returned).then(() => ({ kind: 'settled', rejection: null }), (reason) => ({ kind: 'settled', rejection: crashMessage(reason) }));
        void Promise.race([settled, deadline]).then((outcome) => {
            clearTimeout(timer);
            if (outcome.kind === 'timeout') {
                this.#event({ name: 'surface:unmount-timeout', mountId: this.id });
                return cleanup(null);
            }
            return cleanup(outcome.rejection);
        });
    }
    /** Release services, empty the element, restore its attributes, then free the element and the root claim (R-50 order). */
    #teardown() {
        if (this.#servicesMinted) {
            this.#servicesMinted = false;
            this.#host.releaseServices(this.id);
        }
        this.#el.replaceChildren();
        this.#restoreAttributes?.();
        this.#restoreAttributes = null;
        this.#release();
    }
    /** Free the root claim this mount holds; the surface may still be tearing down in its own element. */
    #releaseRootClaim() {
        if (liveRootMountId === this.id)
            liveRootMountId = null;
    }
    /** Free the element and the root claim this mount holds. */
    #release() {
        busyElements.delete(this.#el);
        this.#releaseRootClaim();
    }
    #finish() {
        this.#resolveReady(this.#state);
        this.#bus.close();
    }
}
//# sourceMappingURL=mount.js.map